import React, { Fragment, useEffect, useState } from "react";
import { CalendarDay } from "./CalendarDay";
import { CalendarMonth } from "./CalendarMonth";
import { CalendarWeek } from "./CalendarWeek";
import { HeaderCalendar } from "../molecules/HeaderCalendar";
import { DateTime } from "luxon";
import { BaseEvent } from "@/app/events/events";

export type CalendarProps = {
  onDateTimeChange?: (startDateTime: DateTime, endDateTime: DateTime) => void;
  onEventClick?: (event: BaseEvent) => void;
  initialDateTime: DateTime;
  events: Array<BaseEvent>;
  loading: boolean;
  hideFilter?: boolean;
  filtersOpen?: () => void;
  functionButtonTopCreate?: () => void;
  functionButtonTime?: () => void;
  functionButtonCalendar?: () => void;
  functionOpenModalFestivos?: () => void;
  functionOpenModalSala?: () => void;
  functionOpenModalGrupos?: () => void;
  functionOpenModalBonos?: () => void;
  funcionOpenKpis?: () => void;
  onMonthChange?: (month: DateTime) => void;
  functionOpenModalActividades?: () => void;
  canCreateActivity?: boolean;
  canEditTimesActivity?: boolean;
  canUploadCalendar?: boolean;
  canEditHolidaysActivity?: boolean;
  canSeeKpis?: boolean;
};

export const Calendar: React.FC<CalendarProps> = ({
  initialDateTime,
  onDateTimeChange,
  onEventClick,
  loading,
  hideFilter,
  filtersOpen,
  functionButtonTopCreate,
  functionButtonTime,
  functionButtonCalendar,
  functionOpenModalFestivos,
  functionOpenModalSala,
  functionOpenModalGrupos,
  functionOpenModalBonos,
  funcionOpenKpis,
  events,
  onMonthChange,
  canCreateActivity,
  canEditTimesActivity,
  canUploadCalendar,
  canEditHolidaysActivity,
  canSeeKpis,
}) => {
  const [viewType, setViewType] = useState<"week" | "day" | "month">("month");
  const [currentDateTime, setCurrentDateTime] = useState<DateTime>(
    initialDateTime ?? DateTime.now()
  );

  useEffect(() => {
    if (typeof onDateTimeChange == "function") {
      if (viewType == "day") {
        onDateTimeChange(currentDateTime, currentDateTime.plus({ days: 1 }));
      }

      if (viewType == "week") {
        onDateTimeChange(currentDateTime, currentDateTime.plus({ weeks: 1 }));
      }

      if (viewType == "month") {
        onDateTimeChange(currentDateTime, currentDateTime.plus({ months: 1 }));
      }
    }
  }, [currentDateTime, viewType, onDateTimeChange]);

  const handleNext = () => {
    if (viewType == "day") {
      setCurrentDateTime(currentDateTime.plus({ days: 1 }).startOf("day"));
    }

    if (viewType == "month") {
      setCurrentDateTime(currentDateTime.plus({ months: 1 }).startOf("month"));
      if (onMonthChange) {
        const newDateTime = currentDateTime
          .plus({ months: 1 })
          .startOf("month");
        onMonthChange(newDateTime);
        setCurrentDateTime(newDateTime);
      }
    }

    if (viewType == "week") {
      setCurrentDateTime(currentDateTime.plus({ weeks: 1 }).startOf("week"));
    }
  };

  const handleNextMonth = () => {
    setCurrentDateTime(currentDateTime.plus({ months: 1 }).startOf("month"));
  };

  const handlePrev = () => {
    if (viewType == "day") {
      setCurrentDateTime(currentDateTime.minus({ days: 1 }).startOf("day"));
    }

    if (viewType == "month") {
      setCurrentDateTime(currentDateTime.minus({ months: 1 }).startOf("month"));
      if (onMonthChange) {
        onMonthChange(currentDateTime.minus({ months: 1 }).startOf("month"));
      }
    }

    if (viewType == "week") {
      setCurrentDateTime(currentDateTime.minus({ weeks: 1 }).startOf("week"));
    }
  };

  const handlePrevMonth = () => {
    setCurrentDateTime(currentDateTime.minus({ months: 1 }).startOf("month"));
  };

  const handleToday = () => {
    const today = DateTime.now();

    if (viewType == "day") {
      setCurrentDateTime(today.startOf("day"));
      if (onMonthChange) {
        onMonthChange(today);
      }
    }

    if (viewType == "month") {
      setCurrentDateTime(today.startOf("month"));
      if (onMonthChange) {
        onMonthChange(today);
      }
    }

    if (viewType == "week") {
      setCurrentDateTime(today.startOf("week"));
      if (onMonthChange) {
        onMonthChange(today);
      }
    }
  };

  const handleViewTypeChange = (viewType: "week" | "day" | "month") => {
    setViewType(viewType);
  };

  return (
    <Fragment>
      <div className="flex flex-col rounded-md">
        <HeaderCalendar
          hideFilter={hideFilter}
          viewType={viewType}
          currentDateTime={currentDateTime}
          onNext={handleNext}
          onPrev={handlePrev}
          onToday={handleToday}
          onViewTypeChange={handleViewTypeChange}
          functionButtonTime={functionButtonTime}
          functionButtonTopCreate={functionButtonTopCreate}
          canCreateActivity={canCreateActivity}
          canEditTimesActivity={canEditTimesActivity}
          canUploadCalendar={canUploadCalendar}
          canEditHolidaysActivity={canEditHolidaysActivity}
          canSeeKpis={canSeeKpis}
          filtersOpen={filtersOpen}
          functionButtonCalendar={functionButtonCalendar}
          functionOpenModalFestivos={functionOpenModalFestivos}
          functionOpenModalSala={functionOpenModalSala}
          functionOpenModalGrupos={functionOpenModalGrupos}
          functionOpenModalBonos={functionOpenModalBonos}
          funcionOpenKpis={funcionOpenKpis}
        />
      </div>
      {loading ? (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-primary"
            role="status"
          >
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
              Loading...
            </span>
          </div>
        </div>
      ) : (
        <Fragment>
          {viewType === "day" && (
            <CalendarDay
              currentDateTime={currentDateTime}
              onEventClick={onEventClick}
              onNextMonth={handleNextMonth}
              onPrevMonth={handlePrevMonth}
              events={events}
              setCurrentDateTime={setCurrentDateTime}
            />
          )}
          {viewType === "week" && (
            <CalendarWeek
              currentDateTime={currentDateTime}
              onEventClick={onEventClick}
              events={events}
            />
          )}
          {viewType === "month" && (
            <CalendarMonth
              currentDateTime={currentDateTime}
              events={events}
              onEventClick={onEventClick}
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};
